/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable func-style */
import React from 'react';

import type { ProductPresentationLabel } from '../components/ProductPresentationSpecs';

interface GroupDefinition<TType extends string, TSpecSet extends readonly string[]> {
    /**
     * Group name that will be used as a key for the group. I.e. "primaryPrice", "secondaryPrice", "totalPrice" etc.
     */
    readonly type: TType;

    /**
     * Set of specs (strings) that define each of the labels in this group.
     */
    readonly specSet: TSpecSet;

    /**
     * Function that creates a label for a given spec, both how to render and what A11y text to use.
     */
    readonly createLabel: (spec: TSpecSet[number]) => ProductPresentationLabel | null;
}

// Helper to create groups with proper type inference
export function defineProductPriceGroup<
    const TType extends string,
    const TSpecSet extends readonly string[],
>(group: GroupDefinition<TType, TSpecSet>): GroupDefinition<TType, TSpecSet> {
    return group;
}

interface RenderedLabel {
    readonly a11y: string;
    readonly render: React.ReactNode[];
}

// Extract spec types for return type inference with typed definitions access
type ExtractSpecTypes<TGroup extends readonly GroupDefinition<any, any>[]> = {
    readonly [K in TGroup[number]['type']]: RenderedLabel;
} & {
    /**
     * Access to the original definitions of the groups.
     * This allows for type-safe access to the original spec definitions, to reuse them when needed.
     */
    readonly definitions: TGroup & {
        readonly [K in TGroup[number]['type']]: Extract<TGroup[number], { type: K }>;
    };
};

// Pure function to render a single group
const renderGroup = <TType extends string, TSpecSet extends readonly string[]>(
    spec: GroupDefinition<TType, TSpecSet>,
    getIdentifier: () => string,
): RenderedLabel => {
    const labels = spec.specSet
        .map(spec.createLabel)
        .filter((label): label is ProductPresentationLabel => label !== null);

    return {
        a11y: labels.map((label) => label.a11y).join(', '),
        render: labels.map((label, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`${getIdentifier()}_${spec.type}_${index}`}>
                {label.render()}
            </React.Fragment>
        )),
    };
};

function renderProductPriceGroups<const TGroups extends readonly GroupDefinition<any, any>[]>(
    getIdentifier: () => string,
    groups: TGroups,
): ExtractSpecTypes<TGroups> {
    const reduced = groups.reduce(
        (acc, group) => ({
            ...acc,
            [group.type]: renderGroup(group, getIdentifier),
        }),
        {} as Record<string, RenderedLabel>,
    );

    const typedDefinitions = groups.reduce(
        (acc, group) => ({
            ...acc,
            [group.type]: group,
        }),
        {} as any,
    );

    return {
        ...reduced,
        definitions: Object.assign(groups, typedDefinitions),
    } as ExtractSpecTypes<TGroups>;
}

export default renderProductPriceGroups;
